import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Plus, Trash2, Building, Warehouse, Globe, X, Save, AlertCircle, HardDrive, Edit3, RefreshCw } from 'lucide-react';
import { locationService } from '../services';
import siteService from '../services/siteService';
import { uyar, hata, onayla } from '../components/GlobalModal';

interface LocationData {
  id: number;
  name: string;
  code?: string;
  description?: string;
  address?: string;
  floor?: string;
  building?: string;
  zone?: string;
  siteId?: number | null;
  isActive: boolean;
}

const Locations = () => {
  const { t } = useTranslation();

  const [locations, setLocations] = useState<LocationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLocId, setEditingLocId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<{
    name: string; code: string; description: string; address: string;
    floor: string; building: string; zone: string; siteId: number | null;
  }>({
    name: '',
    code: '',
    description: '',
    address: '',
    floor: '',
    building: '',
    zone: '',
    siteId: null
  });
  const [sitesList, setSitesList] = useState<{ id: number; name: string }[]>([]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const result = await locationService.getAll();
      if (result.success && result.data) {
        setLocations(result.data);
      }
    } catch (err) {
      console.error(t('locations.loadError'), err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLocations();
    (async () => {
      try {
        const s = await siteService.getDropdown();
        if (s?.success && Array.isArray(s.data)) setSitesList(s.data as any);
      } catch {}
    })();
  }, []);

  const handleAdd = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      address: '',
      floor: '',
      building: '',
      zone: '',
      siteId: null
    });
    setEditingLocId(null);
    setError(null);
    setShowModal(true);
  };

  const handleEdit = (loc: LocationData) => {
    setFormData({
      name: loc.name,
      code: loc.code || '',
      description: loc.description || '',
      address: loc.address || '',
      floor: loc.floor || '',
      building: loc.building || '',
      zone: loc.zone || '',
      siteId: loc.siteId ?? null
    });
    setEditingLocId(loc.id);
    setError(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    setError(null);
    if (!formData.name) {
      setError(t('locations.locationNameRequired'));
      return;
    }

    setSaving(true);
    try {
      if (editingLocId) {
        const result = await locationService.update(editingLocId, formData);
        if (result.success) {
          setShowModal(false);
          loadLocations();
        } else {
          setError(result.message || t('locations.updateError'));
        }
      } else {
        const result = await locationService.create(formData);
        if (result.success) {
          setShowModal(false);
          loadLocations();
        } else {
          setError(result.message || t('locations.createError'));
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('locations.operationError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!(await onayla(t('locations.deleteConfirm')))) return;

    try {
      const result = await locationService.delete(id);
      if (result.success) {
        loadLocations();
      }
    } catch (err) {
      console.error(t('locations.deleteError'), err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">{t('locations.title')}</h1>
          <p className="text-slate-500 text-sm">{t('locations.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadLocations}
            className="bg-slate-100 text-slate-600 px-4 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-slate-200 transition-all"
          >
            <RefreshCw size={18} /> {t('locations.refresh')}
          </button>
          <button
            onClick={handleAdd}
            className="bg-blue-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-blue-700 transition-all shadow-lg shadow-blue-100 active:scale-95"
          >
            <Plus size={18} /> {t('locations.addLocation')}
          </button>
        </div>
      </div>

      {locations.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
          <MapPin className="mx-auto text-slate-300 mb-4" size={48} />
          <h3 className="text-lg font-bold text-slate-600">{t('locations.noLocations')}</h3>
          <p className="text-slate-400 text-sm mt-1">{t('locations.noLocationsDescription')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {locations.map(loc => (
            <div key={loc.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col group hover:border-blue-400 hover:shadow-xl hover:shadow-slate-100 transition-all duration-300">
              <div className="flex justify-between items-start mb-6">
                <div className="p-4 rounded-2xl bg-blue-50 text-blue-600 transition-transform group-hover:scale-110 duration-300 shadow-sm">
                  <Building size={28} />
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(loc)}
                    className="text-slate-300 hover:text-blue-500 p-2 transition-colors hover:bg-blue-50 rounded-lg"
                    title={t('common.edit')}
                  >
                    <Edit3 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(loc.id)}
                    className="text-slate-300 hover:text-red-500 p-2 transition-colors hover:bg-red-50 rounded-lg"
                    title={t('common.delete')}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              <div className="flex-1">
                <h3 className="text-xl font-bold text-slate-800 group-hover:text-blue-600 transition-colors">{loc.name}</h3>
                {loc.code && (
                  <span className="text-xs font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded mt-1 inline-block">{loc.code}</span>
                )}
                {loc.address && (
                  <div className="flex items-start gap-2 text-slate-500 mt-3">
                    <MapPin size={16} className="shrink-0 mt-0.5 text-slate-400" />
                    <p className="text-sm leading-relaxed">{loc.address}</p>
                  </div>
                )}
                {loc.description && (
                  <p className="text-sm text-slate-400 mt-2">{loc.description}</p>
                )}
              </div>

              <div className="mt-8 pt-4 border-t border-slate-50 flex items-center justify-between">
                <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${loc.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {loc.isActive ? t('common.active') : t('common.inactive')}
                </span>
                {(loc.building || loc.floor) && (
                  <span className="text-xs text-slate-400">
                    {loc.building && `${loc.building}`}
                    {loc.floor && ` / ${t('locations.floorLabel', { floor: loc.floor })}`}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-[2rem] w-full max-w-lg shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden border border-slate-100 max-h-[90vh] overflow-y-auto">
            <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/50 sticky top-0">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">{editingLocId ? t('locations.editLocation') : t('locations.addLocation')}</h2>
                <p className="text-sm text-slate-500 mt-1">{t('locations.enterLocationInfo')}</p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-white rounded-full transition-all">
                <X size={24} />
              </button>
            </div>

            <div className="p-8 space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-100 p-3 rounded-xl flex items-center gap-2 text-red-600 text-xs font-bold animate-in shake duration-300">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('locations.locationName')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm font-bold transition-all"
                    placeholder={t('locations.locationNamePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('locations.locationCode')}</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm font-mono transition-all"
                    placeholder={t('locations.locationCodePlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('locations.building')}</label>
                  <input
                    type="text"
                    value={formData.building}
                    onChange={(e) => setFormData({...formData, building: e.target.value})}
                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm transition-all"
                    placeholder={t('locations.buildingPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('locations.floor')}</label>
                  <input
                    type="text"
                    value={formData.floor}
                    onChange={(e) => setFormData({...formData, floor: e.target.value})}
                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm transition-all"
                    placeholder={t('locations.floorPlaceholder')}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('locations.zone')}</label>
                  <input
                    type="text"
                    value={formData.zone}
                    onChange={(e) => setFormData({...formData, zone: e.target.value})}
                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm transition-all"
                    placeholder={t('locations.zonePlaceholder')}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('locations.address')}</label>
                  <textarea
                    rows={2}
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm transition-all resize-none font-medium"
                    placeholder={t('locations.addressPlaceholder')}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">{t('locations.description')}</label>
                  <textarea
                    rows={2}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm transition-all resize-none font-medium"
                    placeholder={t('locations.descriptionPlaceholder')}
                  />
                </div>
                {sitesList.length > 0 && (
                  <div className="col-span-2">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Şantiye / Site</label>
                    <select
                      value={formData.siteId ?? ''}
                      onChange={(e) => setFormData({...formData, siteId: e.target.value ? parseInt(e.target.value) : null})}
                      className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm transition-all font-medium"
                    >
                      <option value="">— Site yok (bağımsız lokasyon) —</option>
                      {sitesList.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="p-8 bg-slate-50 border-t border-slate-50 flex flex-col gap-3 sticky bottom-0">
               <button
                onClick={handleSave}
                disabled={saving}
                className="w-full py-4 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 transition-all shadow-xl shadow-blue-100 font-black text-sm uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
               >
                 {saving ? <RefreshCw className="animate-spin" size={18} /> : <Save size={18} />}
                 {editingLocId ? t('common.update') : t('common.save')}
               </button>
               <button
                onClick={() => setShowModal(false)}
                className="w-full py-4 text-xs font-black uppercase tracking-widest text-slate-500 hover:text-slate-800 transition-colors"
               >
                 {t('common.cancel')}
               </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Locations;
